/**
 * Client requests v2 (docs/ARCHITECTURE-CLIENT-REQUESTS-V2.md): types, API calls, and the
 * upload of a request's files. Files go straight to storage on an address the server minted;
 * big ones through a Google resumable session that survives a reload, small ones by one PUT
 * retried. Phones on clinic Wi-Fi or mobile data get 8 MiB chunks.
 */
import { api } from './api';

export type RequestKind = 'general' | 'creative';
export type RequestStatus = 'new' | 'approved' | 'converted' | 'declined';

export interface RequestFile { id: string; folder: string; name: string; content_type?: string | null; size?: number | null; status: string }
export interface ClientRequest {
  id: string; client_id: string; client_name?: string | null; requested_by: string; requested_by_name: string;
  kind: RequestKind; title: string; brief?: string | null; reference_urls: string[]; status: RequestStatus;
  converted_creative_id?: string | null; converted_creative_code?: string | null; decline_reason?: string | null;
  action_note?: string | null; decided_at?: string | null; created_at: string; last_activity_at?: string | null;
  strategy_id?: string | null; file_count: number; has_voice: boolean; waiting_on?: 'kaf' | 'clinic' | null;
  can_decide: boolean; unread: number;
}
export interface ClientRequestDetail extends ClientRequest {
  files: RequestFile[]; consent_by_name?: string | null; consent_at?: string | null; strategy_code?: string | null;
  /** deciders only: the clinic's strategies and their slots, for the approve picker */
  strategies?: { id: string; code: string; title: string; period_start?: string | null; period_end?: string | null;
    slots: { id: string; type: string; funnel: string; open: boolean }[] }[];
}
export interface NewRequest {
  kind: RequestKind; title?: string; brief?: string; voice_path?: string | null;
  files: { path: string; folder?: string }[]; consent_confirmed: boolean;
}
export interface ApproveBody { note?: string; strategy_id?: string; creative_type?: string; funnel_stage?: string; needs_shoot?: boolean | null; title?: string }

// portal
export const myRequests = async () => (await api.get<ClientRequest[]>('/portal/requests')).data;
export const myRequest = async (id: string) => (await api.get<ClientRequestDetail>(`/portal/requests/${id}`)).data;
export const sendRequest = async (b: NewRequest) => (await api.post<ClientRequestDetail>('/portal/requests', b)).data;
export const addRequestFiles = async (id: string, files: { path: string; folder?: string }[]) =>
  (await api.post<ClientRequestDetail>(`/portal/requests/${id}/files`, { files, consent_confirmed: true })).data;
// staff
export const requestQueue = async (params: { status?: string; kind?: string } = {}) =>
  (await api.get<ClientRequest[]>('/client-requests', { params })).data;
export const staffRequest = async (id: string) => (await api.get<ClientRequestDetail>(`/client-requests/${id}`)).data;
export const approveRequest = async (id: string, b: ApproveBody) => (await api.post<ClientRequestDetail>(`/client-requests/${id}/approve`, b)).data;
export const declineRequest = async (id: string, reason: string) => (await api.post<ClientRequestDetail>(`/client-requests/${id}/decline`, { reason })).data;
export const linkRequest = async (id: string, creative_id: string) => (await api.post<ClientRequestDetail>(`/client-requests/${id}/link`, { creative_id })).data;
export const purgeRequestFile = async (id: string, fid: string) => (await api.post<ClientRequestDetail>(`/client-requests/${id}/files/${fid}/purge`)).data;
// both
export const openRequestFile = async (id: string, fid: string, download = false) =>
  (await api.get<{ url: string }>(`/requests/${id}/files/${fid}/open`, { params: { download } })).data.url;
export const openRequestVoice = async (id: string) => (await api.get<{ url: string }>(`/requests/${id}/voice/open`)).data.url;

/** The server's own message when it gave one, else a plain sentence. */
export function errorText(e: unknown, fallback: string): string {
  const d = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  return typeof d === 'string' ? d : fallback;
}

// ---------------------------------------------------------------- uploads

const CHUNK = 8 * 1024 * 1024;           // a multiple of 256 KiB; small enough for a weak upstream
const BIG = 8 * 1024 * 1024;              // the server opens a resumable session from here up
const KEY = (f: File) => `kafreq:${f.name}:${f.size}:${f.lastModified}`;
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

interface Minted { file_id: string; path: string; upload_url: string; resumable: boolean }

async function mint(file: File, folder: string): Promise<Minted> {
  return (await api.post<Minted>('/portal/requests/upload-url', {
    filename: file.name, content_type: file.type || 'application/octet-stream', size_bytes: file.size, folder,
  })).data;
}

function put(url: string, body: Blob, headers: Record<string, string>, onSent: (n: number) => void, signal?: AbortSignal):
  Promise<{ status: number; range: string | null }> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const stop = () => { xhr.abort(); reject(new DOMException('stopped', 'AbortError')); };
    if (signal) { if (signal.aborted) { stop(); return; } signal.addEventListener('abort', stop, { once: true }); }
    xhr.open('PUT', url);
    Object.entries(headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) onSent(e.loaded); };
    xhr.onload = () => resolve({ status: xhr.status, range: xhr.getResponseHeader('Range') });
    xhr.onerror = () => reject(new Error('network'));
    xhr.ontimeout = () => reject(new Error('timeout'));
    xhr.timeout = 3 * 60 * 1000;
    xhr.send(body);
  });
}

async function offsetOf(url: string, size: number): Promise<number> {
  for (let i = 0; i < 4; i++) {
    try {
      const r = await fetch(url, { method: 'PUT', headers: { 'Content-Range': `bytes */${size}` } });
      if (r.status === 200 || r.status === 201) return size;
      if (r.status === 308) { const m = /bytes=0-(\d+)/.exec(r.headers.get('Range') || ''); return m ? Number(m[1]) + 1 : 0; }
      if ([400, 404, 410].includes(r.status)) return -2;
    } catch { /* retry */ }
    await sleep(500 * 2 ** i);
  }
  return 0;
}

/** Upload one file for a request; returns the path to send with it. Resumes after a reload
 *  (the session is kept in this browser), retries every step, and can be cancelled. */
export async function uploadRequestFile(file: File, folder: string, onPct: (pct: number) => void, signal?: AbortSignal): Promise<string> {
  const type = file.type || 'application/octet-stream';
  let saved: Minted | null = null;
  try { const raw = localStorage.getItem(KEY(file)); if (raw) saved = JSON.parse(raw); } catch { /* none */ }
  if (saved && saved.resumable) {
    const off = await offsetOf(saved.upload_url, file.size);
    if (off === file.size) { onPct(100); try { localStorage.removeItem(KEY(file)); } catch { /* fine */ } return saved.path; }
    if (off === -2) saved = null;
  } else { saved = null; }
  const m = saved || await mint(file, folder);
  if (!saved && m.resumable) { try { localStorage.setItem(KEY(file), JSON.stringify(m)); } catch { /* fine */ } }

  if (!m.resumable || file.size < BIG) {
    for (let i = 0; ; i++) {
      try {
        const r = await put(m.upload_url, file, { 'Content-Type': type }, (n) => onPct(Math.round((n / file.size) * 100)), signal);
        if (r.status >= 200 && r.status < 300) break;
        throw new Error(String(r.status));
      } catch (e) {
        if ((e as DOMException).name === 'AbortError' || i >= 3) throw e;
        await sleep(1000 * 2 ** i);
      }
    }
  } else {
    let offset = Math.max(0, await offsetOf(m.upload_url, file.size));
    let tries = 0;
    while (offset < file.size) {
      const end = Math.min(offset + CHUNK, file.size) - 1;
      try {
        const r = await put(m.upload_url, file.slice(offset, end + 1), { 'Content-Type': type, 'Content-Range': `bytes ${offset}-${end}/${file.size}` },
          (n) => onPct(Math.min(99, Math.round(((offset + n) / file.size) * 100))), signal);
        if (r.status === 200 || r.status === 201) { offset = file.size; break; }
        if (r.status === 308) { const x = /bytes=0-(\d+)/.exec(r.range || ''); offset = x ? Number(x[1]) + 1 : end + 1; tries = 0; continue; }
        throw new Error(String(r.status));
      } catch (e) {
        if ((e as DOMException).name === 'AbortError' || ++tries > 8) throw e;
        await sleep(Math.min(30000, 1000 * 2 ** tries));
        const off = await offsetOf(m.upload_url, file.size);
        if (off === -2) throw new Error('expired');
        offset = Math.max(offset, off);
      }
    }
  }
  try { localStorage.removeItem(KEY(file)); } catch { /* fine */ }
  onPct(100);
  return m.path;
}

/** Keep the phone awake while files upload; released when the upload ends. */
export async function keepAwake(): Promise<() => void> {
  try {
    const nav = navigator as Navigator & { wakeLock?: { request: (t: 'screen') => Promise<{ release: () => Promise<void> }> } };
    const lock = await nav.wakeLock?.request('screen');
    return () => { lock?.release().catch(() => {}); };
  } catch { return () => {}; }
}
